import * as React from 'react'

import PickerSheetLayout from '~/client/src/shared/components/FullScreenPickerSheet/PickerSheetLayout'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import { useSidePanelOverlayRoot } from '~/client/src/shared/components/SidePanel/SidePanelOverlayContext'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ProjectDateStore, {
  MILLISECONDS_IN_MINUTE,
} from '~/client/src/shared/stores/ui/ProjectDate.store'
import { cn } from '~/client/src/shared/utils/cn'

import DateField from './DateField'
import TimeCombobox from './TimeCombobox'
import WeekdayScheduleFields, {
  IWeekdayWindow,
  WeekdayScheduleMode,
} from './WeekdayScheduleFields'

const DEFAULT_START_MINUTES = 6 * 60 // 6:00 AM
const DEFAULT_END_MINUTES = 18 * 60 // 6:00 PM

export interface IIntervalDraft {
  startDate?: number
  endDate?: number
  isDaily?: boolean
}

export interface IIntervalEditorResult {
  startDate: number
  endDate: number
  isDaily: boolean
  /**
   * 1 = Sunday .. 7 = Saturday when the window belongs to one weekday. Absent on
   * a window that recurs every day or names a date, which is how the server
   * tells the three apart.
   */
  dayOfWeek?: number
}

// Both read and write on the PROJECT's clock, not the viewer's: an operating window
// or closure belongs to the site, so the hours an admin picks must mean the same
// thing to everyone. Anchoring on the browser instead would store whatever instant
// matched the author's own wall clock, and every other viewer — and the server —
// would read it back shifted by the offset between them.

const padTwo = (value: number) => String(value).padStart(2, '0')

/**
 * The calendar day a Date stands for on the BROWSER's clock. DateField deals in days,
 * not instants — it hands back local midnight for the cell that was clicked — so the
 * day has to be read off the clock that produced it. Reading it in project time would
 * pick the neighbouring day for a viewer far enough from the project: local midnight
 * Aug 8 in Tokyo is still Aug 7 in Los Angeles.
 */
/** 1 = Sunday .. 7 = Saturday, the axis the server's `dayOfWeek` uses. */
const WEEKDAY_NUMBERS = [1, 2, 3, 4, 5, 6, 7]

function pickedDayISO(date: Date): string {
  return `${date.getFullYear()}-${padTwo(date.getMonth() + 1)}-${padTwo(
    date.getDate(),
  )}`
}

/** Local midnight of the project-local day a timestamp falls on, so the calendar
 *  highlights the day the stored window actually belongs to. */
function projectDayAsLocalDate(
  projectDateStore: ProjectDateStore,
  ms: number,
): Date {
  const [year, month, day] = projectDateStore
    .getDashedFormattedDate(ms)
    .split('-')
    .map(Number)
  return new Date(year, month - 1, day)
}

/** Minutes-from-midnight for the time-of-day of a timestamp, in project time. */
function minutesOfDay(projectDateStore: ProjectDateStore, ms: number): number {
  const [hours, minutes] = projectDateStore.getHoursMinutes(ms)
  return hours * 60 + minutes
}

/** The instant at the picked calendar day and time, on the PROJECT's clock. */
function applyMinutesToDate(
  projectDateStore: ProjectDateStore,
  base: Date,
  minutes: number,
): number {
  const time = `${padTwo(Math.floor(minutes / 60))}:${padTwo(minutes % 60)}:00`
  return projectDateStore.combineISODateTime(pickedDayISO(base), time).getTime()
}

function Field({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('tw:flex tw:flex-col tw:gap-2', className)}>
      <span className="tw:text-sm tw:font-medium tw:text-foreground">
        {label}
      </span>
      {children}
    </div>
  )
}

interface IProps {
  initial?: IIntervalDraft
  projectDateStore: ProjectDateStore
  onSave: (result: IIntervalEditorResult) => void
  onCancel: () => void
  /** Popover (default, table) keeps the inline layout; sheet adds header + footer chrome. */
  layout?: 'popover' | 'sheet'
  /** Sheet header title (e.g. "Select Date closures"). */
  title?: string
  /**
   * Drops the Daily/Date switch and edits a time-of-day window only, for
   * callers that have no dated variant (e.g. the project's weekly schedule).
   */
  isDailyOnly?: boolean
  /**
   * Lets the Daily tab hold a different window per weekday. Hours of operation
   * only: a closure carries a single interval and has no weekday dimension, so
   * the server does not expose one for it.
   */
  isWeekdayScheduleEnabled?: boolean
  /**
   * The weekday windows to open on. Omitted for a resource that states nothing
   * of its own, which opens on Everyday seeded from `initial`.
   */
  initialWeekdays?: IWeekdayWindow[]
  /** Receives the whole week when the Daily tab is in weekday mode. */
  onSaveWeekdays?: (results: IIntervalEditorResult[]) => void
}

function IntervalEditor({
  initial,
  projectDateStore,
  onSave,
  onCancel,
  layout = 'popover',
  title = '',
  isDailyOnly = false,
  isWeekdayScheduleEnabled = false,
  initialWeekdays,
  onSaveWeekdays,
}: IProps) {
  const isSheet = layout === 'sheet'
  // 36px (lg) footer buttons in the sheet; default (32px) in the table popover.
  const buttonSize = isSheet ? 'lg' : 'default'
  // In the mobile sheet, portal the picker popups (time + date) into the SidePanel
  // overlay root so they open in the sheet's stacking context and stay reachable by
  // touch, instead of behind it on document.body. Desktop popover keeps the default.
  // They mount after the open sheet in that root, so they paint above it.
  // Pass the ref object (not `.current`): Base UI resolves it when the popup mounts
  // and falls back to the body, whereas a `null` element would suppress the portal.
  const overlayRoot = useSidePanelOverlayRoot()
  const pickerPortalContainer = isSheet ? (overlayRoot ?? undefined) : undefined
  const [isDaily, setIsDaily] = React.useState(
    isDailyOnly || initial?.isDaily !== false,
  )
  const [weekdayMode, setWeekdayMode] = React.useState<WeekdayScheduleMode>(
    initialWeekdays?.length
      ? WeekdayScheduleMode.Custom
      : WeekdayScheduleMode.Everyday,
  )
  const [startMinutes, setStartMinutes] = React.useState<number>(
    initial?.startDate != null
      ? minutesOfDay(projectDateStore, initial.startDate)
      : DEFAULT_START_MINUTES,
  )
  const [endMinutes, setEndMinutes] = React.useState<number>(
    initial?.endDate != null
      ? minutesOfDay(projectDateStore, initial.endDate)
      : DEFAULT_END_MINUTES,
  )
  // Held as the picked calendar day (local midnight) so the calendar highlights the
  // cell the window belongs to; the project day is what a stored instant means.
  const [startDate, setStartDate] = React.useState<Date>(
    initial?.startDate != null
      ? projectDayAsLocalDate(projectDateStore, initial.startDate)
      : new Date(),
  )
  const [endDate, setEndDate] = React.useState<Date>(
    initial?.endDate != null
      ? projectDayAsLocalDate(projectDateStore, initial.endDate)
      : new Date(),
  )

  // Every weekday carries a window, worked or not, so unchecking a day and
  // checking it again offers what it had rather than a default.
  const [weekdays, setWeekdays] = React.useState<IWeekdayWindow[]>(() =>
    WEEKDAY_NUMBERS.map(dayOfWeek => {
      const stored = initialWeekdays?.find(day => day.dayOfWeek === dayOfWeek)

      return (
        stored ?? {
          dayOfWeek,
          isWorking: !initialWeekdays?.length,
          startMinutes,
          endMinutes,
        }
      )
    }),
  )

  const isWeekdayMode =
    isWeekdayScheduleEnabled &&
    isDaily &&
    weekdayMode === WeekdayScheduleMode.Custom

  /**
   * Carries the window across the switch, as the project's own schedule editor
   * does: Everyday hands its window to every day, and the first worked day hands
   * its window back. Switching to Everyday rewrites the week there too, so a
   * per-day window does not survive the round trip either way.
   */
  const changeWeekdayMode = (nextMode: WeekdayScheduleMode) => {
    if (nextMode === WeekdayScheduleMode.Custom) {
      setWeekdays(current =>
        current.map(day => ({
          ...day,
          isWorking: true,
          startMinutes,
          endMinutes,
        })),
      )
    } else {
      const [primary] = weekdays.filter(day => day.isWorking)

      if (primary) {
        setStartMinutes(primary.startMinutes)
        setEndMinutes(primary.endMinutes)
      }
    }

    setWeekdayMode(nextMode)
  }

  // A week with no day worked states nothing, and saving it would read as
  // clearing the schedule rather than setting one — so it cannot be saved.
  const hasWorkedWeekday = weekdays.some(day => day.isWorking)

  const toggleWeekday = (dayOfWeek: number) =>
    setWeekdays(current =>
      current.map(day =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, isWorking: !day.isWorking }
          : day,
      ),
    )

  const changeWeekdayWindow = (
    dayOfWeek: number,
    nextStart: number,
    nextEnd: number,
  ) =>
    setWeekdays(current =>
      current.map(day =>
        day.dayOfWeek === dayOfWeek
          ? { ...day, startMinutes: nextStart, endMinutes: nextEnd }
          : day,
      ),
    )

  const changeEverydayWindow = (nextStart: number, nextEnd: number) => {
    setStartMinutes(nextStart)
    setEndMinutes(nextEnd)
  }

  const handleSave = () => {
    if (isWeekdayMode) {
      const base = new Date()
      const worked = weekdays.filter(day => day.isWorking)
      const [firstWorked] = worked

      // Every day worked on the same window is the every-day window said the
      // long way. Saved as one, so it reads back as Everyday and the server is
      // not handed seven rows that mean what one row means.
      const isEveryDayAlike =
        worked.length === WEEKDAY_NUMBERS.length &&
        worked.every(
          day =>
            day.startMinutes === firstWorked.startMinutes &&
            day.endMinutes === firstWorked.endMinutes,
        )

      if (isEveryDayAlike) {
        onSaveWeekdays?.([
          {
            startDate: applyMinutesToDate(
              projectDateStore,
              base,
              firstWorked.startMinutes,
            ),
            endDate: applyMinutesToDate(
              projectDateStore,
              base,
              firstWorked.endMinutes,
            ),
            isDaily: true,
          },
        ])
        return
      }

      onSaveWeekdays?.(
        worked.map(day => ({
          startDate: applyMinutesToDate(
            projectDateStore,
            base,
            day.startMinutes,
          ),
          endDate: applyMinutesToDate(projectDateStore, base, day.endMinutes),
          isDaily: true,
          dayOfWeek: day.dayOfWeek,
        })),
      )
      return
    }

    if (isWeekdayScheduleEnabled && isDaily) {
      // Everyday: one window, no weekday — the shape the server reads as
      // "recurs every day".
      const base = new Date()
      onSaveWeekdays?.([
        {
          startDate: applyMinutesToDate(projectDateStore, base, startMinutes),
          endDate: applyMinutesToDate(projectDateStore, base, endMinutes),
          isDaily: true,
        },
      ])
      return
    }

    if (isDaily) {
      const base = new Date()
      onSave({
        startDate: applyMinutesToDate(projectDateStore, base, startMinutes),
        endDate: applyMinutesToDate(projectDateStore, base, endMinutes),
        isDaily: true,
      })
      return
    }
    const start = applyMinutesToDate(projectDateStore, startDate, startMinutes)
    const end = applyMinutesToDate(projectDateStore, endDate, endMinutes)
    onSave({
      startDate: start,
      endDate: start >= end ? start + MILLISECONDS_IN_MINUTE : end,
      isDaily: false,
    })
  }

  const setStartMs = (ms: number) => {
    setStartDate(projectDayAsLocalDate(projectDateStore, ms))
    setStartMinutes(minutesOfDay(projectDateStore, ms))
  }

  const setEndMs = (ms: number) => {
    setEndDate(projectDayAsLocalDate(projectDateStore, ms))
    setEndMinutes(minutesOfDay(projectDateStore, ms))
  }

  // Date intervals must stay ordered with at least a one-minute gap, matching the
  // legacy closure picker: editing the start pushes the end to start + 1m; editing
  // the end pulls the start to end - 1m (the field the user didn't touch moves).
  // Daily windows may run overnight (start > end) and are never adjusted.
  const keepEndAfterStart = (start: number) => {
    if (
      !isDaily &&
      start >= applyMinutesToDate(projectDateStore, endDate, endMinutes)
    ) {
      setEndMs(start + MILLISECONDS_IN_MINUTE)
    }
  }

  const keepStartBeforeEnd = (end: number) => {
    if (
      !isDaily &&
      applyMinutesToDate(projectDateStore, startDate, startMinutes) >= end
    ) {
      setStartMs(end - MILLISECONDS_IN_MINUTE)
    }
  }

  const handleStartMinutesChange = (minutes: number) => {
    setStartMinutes(minutes)
    keepEndAfterStart(applyMinutesToDate(projectDateStore, startDate, minutes))
  }

  const handleStartDateChange = (date: Date) => {
    setStartDate(date)
    keepEndAfterStart(applyMinutesToDate(projectDateStore, date, startMinutes))
  }

  const handleEndMinutesChange = (minutes: number) => {
    setEndMinutes(minutes)
    keepStartBeforeEnd(applyMinutesToDate(projectDateStore, endDate, minutes))
  }

  const handleEndDateChange = (date: Date) => {
    setEndDate(date)
    keepStartBeforeEnd(applyMinutesToDate(projectDateStore, date, endMinutes))
  }

  const handleModeChange = (value: string) => {
    const nextIsDaily = value === 'daily'
    setIsDaily(nextIsDaily)
    // Daily allows reversed (overnight) times; Date does not. Entering Date mode
    // with a reversed pair pushes the end to start + 1m so it shows ordered.
    const start = applyMinutesToDate(projectDateStore, startDate, startMinutes)
    if (
      !nextIsDaily &&
      start >= applyMinutesToDate(projectDateStore, endDate, endMinutes)
    ) {
      setEndMs(start + MILLISECONDS_IN_MINUTE)
    }
  }

  const startTimeField = (
    <Field label={Localization.translator.startTime}>
      <TimeCombobox
        value={startMinutes}
        onChange={handleStartMinutesChange}
        projectDateStore={projectDateStore}
        ariaLabel={Localization.translator.startTime}
        portalContainer={pickerPortalContainer}
      />
    </Field>
  )
  const endTimeField = (
    <Field label={Localization.translator.endTime}>
      <TimeCombobox
        value={endMinutes}
        onChange={handleEndMinutesChange}
        projectDateStore={projectDateStore}
        ariaLabel={Localization.translator.endTime}
        // Daily only: the window is one span of time, so the end reads as a
        // duration from the start and may land on the next day. In date mode
        // the end carries its own date and stands on its own.
        fromMinutes={isDaily ? startMinutes : undefined}
        portalContainer={pickerPortalContainer}
      />
    </Field>
  )

  const formContent = (
    <div className="tw:flex tw:flex-col tw:gap-5 tw:px-3">
      {!isDailyOnly && (
        <Tabs
          value={isDaily ? 'daily' : 'date'}
          onValueChange={handleModeChange}
        >
          <TabsList className="tw:w-full">
            <TabsTrigger value="daily">
              {Localization.translator.daily}
            </TabsTrigger>
            <TabsTrigger value="date">
              {Localization.translator.date}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {isDaily && isWeekdayScheduleEnabled ? (
        <WeekdayScheduleFields
          days={weekdays}
          mode={weekdayMode}
          everydayStartMinutes={startMinutes}
          everydayEndMinutes={endMinutes}
          projectDateStore={projectDateStore}
          onModeChange={changeWeekdayMode}
          onToggleDay={toggleWeekday}
          onDayWindowChange={changeWeekdayWindow}
          onEverydayWindowChange={changeEverydayWindow}
          portalContainer={pickerPortalContainer}
        />
      ) : isDaily ? (
        <div className="tw:flex tw:flex-col tw:gap-5">
          {startTimeField}
          {endTimeField}
        </div>
      ) : (
        <div className="tw:flex tw:flex-col tw:gap-5">
          <div className="tw:flex tw:items-end tw:gap-2">
            <Field
              className="tw:min-w-0 tw:flex-1"
              label={Localization.translator.startDate}
            >
              <DateField
                value={startDate}
                onChange={handleStartDateChange}
                projectDateStore={projectDateStore}
                portalContainer={pickerPortalContainer}
              />
            </Field>
            <div className="tw:w-[132px]">{startTimeField}</div>
          </div>
          <div className="tw:flex tw:items-end tw:gap-2">
            <Field
              className="tw:min-w-0 tw:flex-1"
              label={Localization.translator.endDate}
            >
              <DateField
                value={endDate}
                onChange={handleEndDateChange}
                projectDateStore={projectDateStore}
                portalContainer={pickerPortalContainer}
              />
            </Field>
            <div className="tw:w-[132px]">{endTimeField}</div>
          </div>
        </div>
      )}
    </div>
  )

  const footerButtons = (
    <>
      <Button variant="outline" size={buttonSize} onClick={onCancel}>
        {Localization.translator.cancel}
      </Button>
      <Button
        size={buttonSize}
        // A custom week with no day worked has nothing to save.
        disabled={isWeekdayMode && !hasWorkedWeekday}
        onClick={handleSave}
      >
        {Localization.translator.save}
      </Button>
    </>
  )

  if (isSheet) {
    return (
      <PickerSheetLayout
        title={title}
        onClose={onCancel}
        heightMode="content"
        footer={
          <div className="tw:flex tw:justify-end tw:gap-2">{footerButtons}</div>
        }
      >
        <div className="tw:py-3">{formContent}</div>
      </PickerSheetLayout>
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:py-3">
      {formContent}
      <div className="tw:flex tw:justify-end tw:gap-2 tw:border-0 tw:border-t tw:border-solid tw:border-border tw:px-3 tw:pt-2.5">
        {footerButtons}
      </div>
    </div>
  )
}

export default IntervalEditor
